/**
 * chartpane.ts — one chart, its series, and its own primitives.
 *
 * WHY THIS EXISTS
 * ---------------
 * `main.ts` held a SINGLE module-level chart: `let chart`, `let candleSeries`,
 * `let volumeSeries`, plus five `const xPrimitive = new XPrimitive()` singletons, with
 * `el.tf.value` read directly in a dozen places. Roughly 80 call sites assumed "there is one
 * chart", so a 3-pane layout is not a UI addition — it is converting a singleton into an
 * instance.
 *
 * The alternative — duplicating the chart construction and overlay wiring per pane — would create
 * three drifting copies of the ICT rendering path. That is the second-source-of-truth failure this
 * repo forbids for ICT logic (`chartapp/api/main.py`: "If a JS file ever contains an ICT threshold,
 * the app has become a second source of truth and is worse than no app at all"), and it would
 * apply to the geometry as well: three places for a range level to be drawn slightly differently.
 *
 * So: one implementation, N instances.
 *
 * WHAT A PANE OWNS
 * ----------------
 *   * its own `IChartApi`, candle series and volume series
 *   * its own FIVE primitives — this is the part that makes the class necessary rather than
 *     convenient. `attachPrimitive` binds a primitive to ONE series, and three of them
 *     (`Ticket`, `Drawing`, and the pointer-wired ones) install pointer listeners in
 *     `attached()`. Sharing one primitive across three panes would attach it three times,
 *     stacking duplicate handlers so a single drag moves an anchor three times — the exact
 *     defect the existing "attached ONCE here rather than per refresh" comments guard against,
 *     one dimension up.
 *   * its own timeframe. The ASSET stays global (all three panes show one symbol, which is what
 *     ICT multi-timeframe analysis means) and so does the source.
 *   * its own overlay toggles, so ICT analysis is per-timeframe — a dealing range on 1W is a
 *     different claim from one on 15m, and conflating them would make the panes decorative.
 *
 * WHAT A PANE DOES NOT OWN
 * ------------------------
 * The order ticket, positions and account panel. Verified: `ticket.ts` contains ZERO references
 * to timeframe. Prices are prices — an entry at 73.163 is the same order whether you found it on
 * 1W or 15m — so binding the money path to a pane would invent an ambiguity that does not exist.
 * This is why no "active pane" concept is needed.
 */
import {
  ColorType,
  CrosshairMode,
  createChart,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type PriceLineOptions,
  type SeriesMarker,
  type Time,
  type CandlestickData,
  type TickMarkType,
  type UTCTimestamp,
} from 'lightweight-charts';

import { BreakPrimitive } from './breakprimitive';
import {
  axisTickLabel,
  crosshairTimeLabel,
  needsClockOnAxis,
  zoneTag,
  zoneTagTitle,
} from './axistime';
import { DrawingPrimitive } from './drawingprimitive';
import { LevelPrimitive, type AccountLevel } from './levelprimitive';
import { barSeconds } from './livepoll';
import { priceFormatFor } from './pricescale';
import { RangePrimitive } from './rangeprimitive';
import type { Anchor, DrawingsPayload } from './drawings';

/** An OHLCV row as the chart consumes it. */
export type Bar = { time: number; open: number; high: number; low: number; close: number };

/** An INSIDE candle relative to a given anchor: contained within the anchor's high-low range. */
export function isInsideBar(b: Bar, anchor: Bar | undefined): boolean {
  return anchor !== undefined && b.high <= anchor.high && b.low >= anchor.low;
}

/** Which bars are inside candles, by a RUNNING ANCHOR rather than pairwise comparison.
 *
 * Andrei, 2026-08-02: "the provided example shows that it 'hides' all internal candles after the one on
 * the left till we have a candle that interacts with the last colored candle liquidity higher or lower."
 *
 * That is a different rule from the one I first shipped, and his reference screenshot is the evidence.
 * Pairwise `high <= prev.high && low >= prev.low` re-anchors on EVERY bar, so a slowly-narrowing
 * sequence keeps producing "normal" candles — each one is only compared to the bar immediately before
 * it. The reference instead holds the last COLOURED (non-inside) candle as the anchor and hides every
 * subsequent bar until one takes out that anchor's high or low. The anchor is liquidity; the run of
 * hidden bars is everything that failed to interact with it.
 *
 * Consequence, and the reason it matters beyond styling: a compression run is shown as ONE reference
 * candle plus its dependents, which is exactly the structure `OB_algorithm.md` §A2 relies on when it
 * says the imbalance is measured from candle `N` THROUGH the inside candles, not from the inside one.
 *
 * Returns a parallel boolean array so the caller stays a simple map. O(n), single pass.
 */
export function insideFlags(bars: readonly Bar[]): boolean[] {
  const flags: boolean[] = new Array(bars.length).fill(false);
  let anchor: Bar | undefined;
  for (let i = 0; i < bars.length; i += 1) {
    const b = bars[i];
    if (isInsideBar(b, anchor)) {
      flags[i] = true;
      // Anchor UNCHANGED: it stays the reference until something interacts with its liquidity.
      continue;
    }
    anchor = b;
  }
  return flags;
}

/** The chart canvas colour. ONE definition, referenced by both the chart options and the hollow
 *  inside-candle fill — those two must agree by construction or "hollow" stops being hollow. */
const CHART_BG = '#131722';

/** Inside candles are drawn HOLLOW: fill = the canvas, thin dark border.
 *
 *  Andrei, 2026-08-02, with a light-theme reference screenshot of the same indicator: "here's how this
 *  indicator should look... on my dark theme internal candles should be darker and more 'invisible'
 *  for my eye."
 *
 *  The reference settles the style question I had been guessing at. In it, inside candles are neither
 *  a muted green nor a muted red — they are HOLLOW: body filled with the exact background white,
 *  outlined by a 1 px near-black hairline, wick the same hairline. So the candle is defined only by its
 *  outline, and direction is deliberately NOT encoded — an inside candle has no directional meaning,
 *  which is the whole point of §A2 excluding it from order-block marking.
 *
 *  My two earlier attempts both tried to keep the direction hue (dim teal / dim red) and both failed
 *  his eye test. That instinct was wrong: dimming a colour still draws attention to it. Removing the
 *  fill entirely is what makes it recede.
 *
 *  DARKER THAN THE BODY, NOT INVISIBLE. `INSIDE_EDGE` is only a little above the background — enough
 *  to trace the bar deliberately, not enough to register when scanning ("still see these candles just
 *  in case", 2026-08-02). On the light reference the equivalent contrast is much higher because the
 *  background is white; on a dark canvas the same *relationship* means a dim outline, not a black one,
 *  which would vanish completely. */
const INSIDE_FILL = CHART_BG;
const INSIDE_EDGE = '#4a5262';

function tintInside(b: Bar, inside: boolean): CandlestickData<UTCTimestamp> {
  const row: CandlestickData<UTCTimestamp> = {
    time: b.time as UTCTimestamp,
    open: b.open,
    high: b.high,
    low: b.low,
    close: b.close,
  };
  if (inside) {
    row.color = INSIDE_FILL;
    row.borderColor = INSIDE_EDGE;
    row.wickColor = INSIDE_EDGE;
  }
  return row;
}

/** What a consumer OUTSIDE this class may do with a pane's candle series.
 *
 * `setData` and `update` are deliberately ABSENT. They are the two candle-WRITE operations, and they
 * belong to `setBars` / `updateBar`, which apply the inside-candle tint and keep `bars` in step.
 *
 * WHY A TYPE AND NOT A CONVENTION: `main.ts` wrote candles both ways for months — `pane.setBars()` in
 * `loadContextPanes()` and a raw `candleSeries.setData()` in `loadBars()`. Both compiled, both looked
 * right in review, and the tint silently applied to the two context panes but NOT to the execution
 * pane Andrei actually watches. He reported bright candles twice and I "fixed" the colours twice,
 * because the colours were never the bug — a second write path was.
 *
 * A grep test would have caught the recurrence. This makes it not compile, which is the difference
 * between a lint and an invariant. Everything the order ticket legitimately needs —
 * price lines, coordinate mapping, primitives, `dataByIndex` — stays available.
 */
export type PaneSeriesReadonly = Omit<ISeriesApi<'Candlestick'>, 'setData' | 'update'>;

/** Which overlays a pane draws. Per pane, not global — see the module docstring. */
export type PaneOverlays = {
  swings: boolean;
  /** The structure dots -- SEPARATE from `swings`, and that separation is the bug fix.
   *
   * Andrei, 2026-08-10, looking at his own chart: "why i don't see these dots, what should be
   * selected?" The answer at the time was "tick swings", which is a bad answer: `swings` draws
   * ~1,300 raw pivots on a 15m pane and he had turned it off precisely to clear that mat. The
   * dots are the market structure he asked us to draw, so they cannot ride the checkbox whose
   * job is to hide clutter. Same fetch as `swings` (the points arrive on that payload), own
   * gate -- see `swingsDraw`. */
  market_structure: boolean;
  breaks: boolean;
  fvg: boolean;
  order_blocks: boolean;
  ranges: boolean;
  liquidity: boolean;
  /** The composite: break + IDM + sweep + entry zone, from ict.setup. */
  setup: boolean;
  /** Closed-bar LTF EVIDENCE for a manual review (card `t_bf04f7ec`) — one marker per fact, not a
   *  finding and never a signal.
   *
   *  IT IS A KEY HERE BECAUSE `main.ts`'s catalogue ALREADY RENDERS IT (card `t_fc464fed`,
   *  2026-09-01). `fefd5d36` added the row to `OVERLAYS` and the marker bucket to this class but
   *  not to this record, and the checkbox loop is `OVERLAYS.map(o => o.id as keyof PaneOverlays)` —
   *  so the box drew, `state[...].overlays.ltf_evidence` was `undefined`, and `loadPaneState`'s
   *  `Object.keys(DEFAULT_OVERLAYS)` walk DROPPED the tick on every reload. The layer worked within
   *  a session and silently forgot itself, which is worse than an absent toggle. Found by gate 7,
   *  whose `toHaveCount(8)` was the only thing in the repo counting the rendered boxes. */
  ltf_evidence: boolean;
  /** Session boxes + the Asia model, `ict.session_model` (card `t_5b2017b5`). */
  sessions: boolean;
  /** RTH / Asia→RTH range / crypto windows / PDH-PDL / open gaps, `ict.session_levels`
   *  (card `t_2d80caad`). */
  session_levels: boolean;
  /** Full-course static structure, `ict.static_structure` (card `t_fb5e844d`). */
  static_structure: boolean;
  /** BP channel Asia-sweep objects, `ict.bp_session` (card `t_5ee634f0`). */
  bp_sessions: boolean;
  /** BP channel imbalance, merged runs + unfilled part, `ict.bp_imbalance` (card `t_28a039b9`). */
  bp_imbalance: boolean;
  /** BP channel 021 IDM, body-close formation + body break, `ict.bp_idm` (card `t_6f7db3f5`). */
  bp_idm: boolean;
  /** BP channel 023 order block, regular / engulfed / breaker, `ict.bp_ob` (card `t_d894c573`). */
  bp_ob: boolean;
  /** The research-only experiment overlay: event markers, boxes and the selected event's levels
   *  and windows (card t_42cdbde6). Ticked by a navigator jump on `pane-ltf`. */
  experiment: boolean;
};

/**
 * The default overlay set for a NEW pane: THE SETUP, AND NOTHING ELSE (2026-08-24, card
 * t_74464b11).
 *
 * INVERTED from `swings + market_structure + ranges`. Andrei, with an `xyz:XYZ100` screenshot:
 * *"UI/UX, который в настоящее время выглядит грязно. куча зон и нихрена не понятно. что надо
 * сделать, чтобы сетап был виден чисто?"* Counted off that screenshot, drawn SIMULTANEOUSLY on
 * four panes: SH 30983, OTE major, OTE local, EQ local, H7 30260 (open leg), LT 29210 (open leg),
 * SL 27029, IDM, `wait short OB`, `wait long FVG`, the week range. The setup — the thing that
 * decides money — was visually indistinguishable from the diagnostics drawn over it.
 *
 * THE PREVIOUS DEFAULT WAS NOT WRONG, IT ANSWERED A DIFFERENT QUESTION. `swings + ranges` (his
 * own 2026-07-29 request) is what you want while READING the chart: "where are we in the range
 * and what structure got us here". `setup` is what you want while DECIDING. A default can only
 * serve one of the two, and the one it must serve is the decision — the reading is one click
 * away, and every one of these controls is still here.
 *
 * NOT A "CLEAN MODE". There is no second flag, no preset and no profile: the values below are
 * the only thing that changed. A mode would be a second source of truth about what is drawn,
 * which is precisely the class of bug this module's other docstrings keep recording.
 *
 * WHAT THIS DOES NOT TOUCH: ENTRY / SL / TP / LIQ. Those are `livelevels.levelsForPosition` and
 * `ticket.ts` through `tpDrawLevels`/`tkDrawLines`, neither of which reads `PaneOverlays` at all
 * — verified by grep, and pinned by `overlaydefaults.test.ts`. The money path is drawn on every
 * setting of every box here. The `setup` overlay draws the entry ZONE, its IDM level and the
 * proximal-edge line; that is analysis geometry, not the live order graph.
 *
 * ONE CONSEQUENCE WORTH NAMING: the swing zigzag (card t_13260999) rides `draw.pivots`, i.e. the
 * `swings` gate, so it is now off on a fresh load. It is deployed and one click away.
 */
export const DEFAULT_OVERLAYS: PaneOverlays = {
  swings: false,
  // OFF with `swings`, and it is still a SEPARATE key: the 2026-08-10 split exists so the dots
  // survive clearing the ~1,300-pivot mat, and that independence is unaffected by which value
  // each one starts at.
  market_structure: false,
  breaks: false,
  fvg: false,
  order_blocks: false,
  ranges: false,
  liquidity: false,
  // THE ONLY ONE ON. It is the composite (break + IDM + sweep + zone to wait at) that the method
  // actually trades, so it is the one layer that belongs on screen before anything is ticked.
  setup: true,
  // OFF: a review layer that arrives unasked competes with the finding layers for attention, and
  // this one draws one marker per FACT rather than one box per setup. `main.ts`'s catalogue comment
  // already said "OFF BY DEFAULT (`chartpane.DEFAULT_OVERLAYS` is the only place that decision
  // lives)" while this record had no such key at all — the default was `undefined`, which renders
  // the checkbox indeterminate and is neither on nor off.
  ltf_evidence: false,
  // OFF: context boxes for the session model (card `t_5b2017b5`), a review layer like the one above.
  sessions: false,
  // OFF: the full-course clock levels (card `t_2d80caad`), a review layer like the two above.
  session_levels: false,
  // OFF: the full-course static structure (card `t_fb5e844d`) is a review layer too, and it needs
  // a fractal `n` the author never states -- nothing is drawn until one is chosen.
  static_structure: false,
  // OFF: the BP channel's Asia-sweep objects (card `t_5ee634f0`), a review layer like the rest.
  bp_sessions: false,
  // OFF: the BP channel's imbalance (card `t_28a039b9`), a review layer like the rest.
  bp_imbalance: false,
  // OFF: the BP channel's 021 IDM (card `t_6f7db3f5`), a review layer like the rest.
  bp_idm: false,
  // OFF: the BP channel's 023 order block (card `t_d894c573`), a review layer like the rest.
  bp_ob: false,
  // OFF: the experiment overlay (card t_42cdbde6) draws only once an experiment is open on the
  // research source; the navigator ticks it on `pane-ltf` when it jumps.
  experiment: false,
};

/** Every timeframe the Hyperliquid source serves. Andrei: all of them, on every pane. */
export const ALL_TIMEFRAMES = ['1M', '1W', '1D', '4h', '1h', '15m', '5m', '1m'] as const;
export type Timeframe = (typeof ALL_TIMEFRAMES)[number];

/**
 * WHAT ONE PANE RESTORES FROM STORAGE, given the two saved blobs and this pane's spec default.
 *
 * A PURE FUNCTION, in this module and not in `main.ts`, for the reason `swingsDraw` above is:
 * `loadPaneState` needs `localStorage` and `main.ts` calls `createChart` at import time, so the
 * decision would otherwise be reachable only by a substring grep over source text — and a grep
 * cannot tell "the v1 overlays are skipped" from "a comment says they are skipped". This card's
 * whole finding was a guard of exactly that shape (`overlaydefaults.test.ts` asserting `on: true`
 * against a field nothing read), so the fix does not get to ship with one.
 *
 * THE MIGRATION IT ENCODES (2026-08-24, card t_74464b11). `DEFAULT_OVERLAYS` was inverted so a
 * fresh chart shows the setup alone; but every browser that has ever opened this app has a saved
 * overlay set, restored key-by-key, so the new default would reach nobody. `panes.v2` is a fresh
 * key, and when it is absent the v1 blob contributes its TIMEFRAME ONLY:
 *
 *   * tf SURVIVES — it is a layout choice ("4h in the middle"), unrelated to this card, and
 *     dropping it would be a regression dressed as a migration. Still validated, because it
 *     becomes a URL path segment.
 *   * overlays DO NOT — inheriting them is the single thing the migration exists to prevent.
 *
 * `v2` wins whenever it exists, including when it is EMPTY of overlays: once the user has clicked
 * a checkbox under the new defaults, v1 must never speak again, or a deliberate re-tick of
 * `swings` would be silently overwritten by a blob from before the change.
 */
export function restorePaneOverlayState(
  specTf: string,
  v2: { tf?: unknown; overlays?: unknown } | undefined,
  v1: { tf?: unknown; overlays?: unknown } | undefined,
  v2Present: boolean,
): { tf: string; overlays: PaneOverlays; migrated: boolean } {
  const migrated = !v2Present;
  const saved = v2Present ? v2 : v1;
  const out = { tf: specTf, overlays: { ...DEFAULT_OVERLAYS }, migrated };
  if (!saved) return out;
  if (typeof saved.tf === 'string' && (ALL_TIMEFRAMES as readonly string[]).includes(saved.tf)) {
    out.tf = saved.tf;
  }
  // THE ONE-WAY DOOR. On the v1 path this block is skipped even though `saved.overlays` is very
  // likely a well-formed OLD overlay set — that is precisely why.
  if (!migrated && saved.overlays && typeof saved.overlays === 'object') {
    const ov = saved.overlays as Record<string, unknown>;
    // Key-by-key, so a saved blob missing a newly-added overlay does not produce `undefined`
    // where a boolean is expected — the checkbox would render indeterminate and the overlay
    // would neither draw nor report why.
    for (const k of Object.keys(DEFAULT_OVERLAYS) as (keyof PaneOverlays)[]) {
      if (typeof ov[k] === 'boolean') out.overlays[k] = ov[k] as boolean;
    }
  }
  return out;
}

/** Does this timeframe need a CLOCK on the axis, not just a date?
 *
 * DEFINED IN `axistime.ts` AND RE-EXPORTED HERE (2026-08-25, card t_69d27c87). The predicate now
 * decides two things — whether the axis carries a clock, and which ZONE its labels are spelled in —
 * and the formatters that answer the second live in `axistime.ts`. Defining it here and importing
 * them would make the two modules import each other. Re-exported rather than moved outright so
 * every existing consumer (and `chartpane.test.ts`) keeps its import.
 */
export { needsClockOnAxis } from './axistime';

/**
 * WHICH PARTS OF THE SWINGS PAYLOAD A PANE DRAWS, and whether it needs the payload at all.
 *
 * TWO INDEPENDENT GATES OVER ONE FETCH. The server places the structure points and sends them on
 * the SAME response as the raw pivots, so there is one request; but `swings` (the ~1,300-pivot mat)
 * and `market_structure` (the dots) are separate things to look at, and Andrei proved it by turning
 * the first off and losing the second: "why i don't see these dots, what should be selected?"
 *
 * A FUNCTION RATHER THAN TWO `if`s IN `main.ts`. `refreshOverlaysOnce` needs a chart, a DOM and a
 * backend, so the four combinations of these two booleans are not otherwise reachable from a test --
 * and "the dots draw when swings is OFF" is the exact property that regressed. Pure, so it is.
 */
export function swingsDraw(o: Pick<PaneOverlays, 'swings' | 'market_structure'>): {
  fetch: boolean;
  pivots: boolean;
  dots: boolean;
} {
  return { fetch: o.swings || o.market_structure, pivots: o.swings, dots: o.market_structure };
}

// ── WHAT WINDOW A PANE OPENS ON ─────────────────────────────────────────────────────────────────
//
// Andrei, 2026-08-10: "please, change default scale for the panes: 1W - last 3 years, 1d - last 4
// months, 1d - leave as-is" -- the last clause names 1d twice; the panes are 1W / 1D / 15m and the
// first two clauses have already spoken for 1W and 1D, so the one left to leave alone is 15m, at
// the week it already opens on. 15m IS UNCHANGED, and that is a requirement rather than an omission.
//
// Superseded: 1W on 6 months and 1D on 3 months (2026-08-09, also his). Same shape of request, new
// durations, so only the numbers move.
//
// CALENDAR SECONDS, not a bar count, and that is the one real decision here. "Four months" is a
// statement about the CLOCK: 4 months of a 24/7 perp is ~122 daily bars, of an `xyz:` equity ~84,
// and a bar count would show the equity six calendar months while claiming four. Months are
// 30.4375 days (365.25/12) and a year is 365.25 days -- the convention this app already uses.
//
// LIVES HERE, WITH THE PANE. The durations used to sit in `overlays.ts` beside the detector
// renderers, which is the wrong neighbourhood: what viewport a pane opens on is a fact about the
// PANE, exactly like `needsClockOnAxis` above and `insideWindow` below, and none of it is overlay
// geometry. THIS IS THE ONLY COPY -- the pair left behind in `overlays.ts` was deleted (verified
// 2026-08-19: `grep PANE_WINDOW_SECONDS chartapp/web/src/overlays.ts` returns nothing, and
// `panewindow.test.ts` still pins that `main.ts` imports from here). Two live copies of a duration
// is how one of them silently becomes wrong.
const DAY_SECONDS = 86400;
const MONTH_SECONDS = 30.4375 * DAY_SECONDS;
const YEAR_SECONDS = 365.25 * DAY_SECONDS;

export const PANE_WINDOW_SECONDS: Record<string, number> = {
  '1W': Math.round(3 * YEAR_SECONDS),
  '1D': Math.round(4 * MONTH_SECONDS),
  // TEN DAYS -- Andrei, 2026-08-19: "we need slightly more data on 15m to see the entire week and
  // forming ICT the profile better something beyond this lines". This SUPERSEDES his earlier "exactly
  // one week" instruction, which the comment here used to cite; he is overriding himself, and a
  // comment quoting a withdrawn instruction is the stale-comment defect (CLAUDE.md 0c item 3).
  //
  // Ten days is a week PLUS context: the weekly-profile row beside this pane eliminates profiles from
  // Monday's open onwards, and at exactly 7 days Monday sits on the left edge with nothing before it
  // to compare against. Unrounded because it is exact.
  '15m': 10 * DAY_SECONDS,
  // THREE DAYS on 5m, and the number is DERIVED from the budget Andrei already approved rather
  // than chosen (card t_e7baebb0, 2026-08-24). He asked only for 5m to be a supported timeframe;
  // he has separately and repeatedly objected to the WEIGHT of what a pane loads, so the window
  // is the largest whole number of days that fits inside the 15m pane's already-measured cost.
  //
  // The pixel budget is a property of the PANE (~760 px), not of the timeframe, so the bar count
  // carries across and the calendar span follows from it. Measured on this box against a real
  // `/api/bars` (`process/reviews/t_e7baebb0/measure_5m_payload.py`):
  //
  //     BTC/15m limit=960  ->  77,447 B   960 bars   9.99 d   0.79 px/bar   <- already shipped
  //     BTC/5m  limit=864  ->  69,765 B   864 bars   3.00 d   0.88 px/bar   <- this window
  //     BTC/5m  limit=2880 -> 231,046 B  2880 bars  10.00 d   0.26 px/bar   <- 10 days, rejected
  //     ETH/5m  limit=864  ->  66,309 B   864 bars   3.00 d   0.88 px/bar
  //
  // So 5m costs LESS than the 15m pane already does, at a slightly better px/bar. Copying 15m's
  // TEN DAYS instead would be 2880 bars and 231 KB — 3x the payload for a pane at 0.26 px/bar,
  // i.e. ~74% of it sub-pixel, which is precisely the waste the 2026-07-31 row cap was measured
  // to remove. Three days is also enough intraday context to read more than two full sessions.
  //
  // NOT `1m`, which has no entry here and must not get one by analogy: 600 bars is 10 hours of 1m
  // and the server's default already exceeds any window worth opening on, so `paneBarLimit`
  // correctly returns null for it and the pane fits its series.
  '5m': 3 * DAY_SECONDS,
};

/**
 * How many bars `/api/bars` must serve for this pane's opening window to be drawable.
 *
 * THE VIEWPORT WAS NEVER THE ONLY CAP. `BARS_LIMIT_DEFAULT = 600` is 6.25 days of 15m, so the 15m
 * pane could not show a full week even while `PANE_WINDOW_SECONDS` asked for seven: the series it was
 * handed was SHORTER than the window, `paneWindowRange` correctly returned null, and the pane fell
 * back to fitting 6.25 days. Raising one without the other changes nothing.
 *
 * DERIVED, NOT A SECOND TABLE. The count is the window divided by the bar length, both of which are
 * already stated once -- `PANE_WINDOW_SECONDS` here and `barSeconds` in `livepoll.ts`, which parses
 * the timeframe rather than mapping it. A hand-written `{'15m': 960}` beside a `{'15m': 10 * DAY}` is
 * two spellings of one fact, which is how one of them goes wrong.
 *
 * NULL means "the server's default is enough", which is true of every pane except 15m and 5m: 600
 * bars is 100 days of 4h and 1.6 years of 1D, well past their windows, but only 6.24 days of 15m
 * and 2.08 days of 5m — both short of what those panes open on. So this returns a number only when
 * the pane genuinely needs more, and the 2026-07-31 payload cut stands everywhere else.
 *
 * MEASURED COST, in the same units as the 2026-07-31 note (`main.py` BARS_LIMIT_DEFAULT), because the
 * cap Andrei asked for exists for a measured reason and he is entitled to the number:
 *     BTC/15m  limit=600 -> 48,648 B  600 bars  6.24 d  1.27 px/bar
 *     BTC/15m  limit=960 -> 77,448 B  960 bars  9.99 d  0.79 px/bar
 *     ETH/15m  limit=600 -> 46,247 B      "        "        "
 *     ETH/15m  limit=960 -> 73,607 B  960 bars  9.99 d  0.79 px/bar
 * +28.8 KB on ONE pane of three (+59% of that pane, not of the load), and no extra venue traffic:
 * `/api/bars` reads `bars_cache_backend`, whose Query is newest-first with a `Limit`, so a bigger
 * limit is a bigger single Query against a cache already holding ~58 days of 15m -- not a bigger scan.
 * 0.79 px/bar is BELOW one pixel per bar in a 760px pane, and that is the honest cost of seeing the
 * whole week: it is still 5x the 0.15 px/bar the 2026-07-31 cut was measured against.
 */
export function paneBarLimit(tf: string): number | null {
  const span = PANE_WINDOW_SECONDS[tf];
  const bar = barSeconds(tf);
  if (span === undefined || bar === null || bar <= 0) return null;
  const need = Math.ceil(span / bar);
  return need > SERVER_BARS_LIMIT_DEFAULT ? need : null;
}

/**
 * `chartapp/api/main.BARS_LIMIT_DEFAULT`, mirrored so `paneBarLimit` can tell "more than the server
 * would give me anyway" from "less". Pinned by `panewindow.test.ts` against the Python constant, so
 * the mirror cannot drift silently.
 */
export const SERVER_BARS_LIMIT_DEFAULT = 600;

/**
 * The TIME RANGE one pane should show on load, or null for "show every bar you have".
 *
 * Anchored on the LAST bar and counted backwards, never on "now": a stale or thinly-traded series
 * would otherwise open on a window with no candles in it at all. It also removes the empty right
 * half of Andrei's 15m pane -- the candles ended mid-pane and the vacant space behind an extended
 * zone was part of why the labels collided.
 *
 * NULL, meaning fall back to fitting the series, in three cases, all of them honest absences rather
 * than a guess: no rule for this timeframe (a pane can be switched to 4h or 1m from its own header
 * select), fewer than two bars to measure, or a series SHORTER than the requested window. A freshly
 * listed perp with four days of 15m bars cannot show a week, and stretching four days across a
 * week-wide axis would claim data that does not exist.
 */
export function paneWindowRange(
  tf: string,
  bars: readonly { time: number }[],
): { from: number; to: number } | null {
  const span = PANE_WINDOW_SECONDS[tf];
  if (span === undefined || bars.length < 2) return null;
  const first = bars[0].time;
  const last = bars[bars.length - 1].time;
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;
  if (last - first <= span) return null;
  return { from: last - span, to: last };
}

/**
 * Shared chart construction options.
 *
 * ONE definition for all panes. Hoisted rather than repeated because the `rightOffset` below is
 * load-bearing, not cosmetic, and three copies of a load-bearing constant is how one of them
 * silently becomes 0.
 */
function chartOptions(tf: string) {
  return {
    layout: {
      // CHART_BG, not a literal: the hollow inside-candle fill is derived from this exact value.
      background: { type: ColorType.Solid, color: CHART_BG },
      textColor: '#d1d4dc',
    },
    grid: {
      vertLines: { color: '#1e222d' },
      horzLines: { color: '#1e222d' },
    },
    crosshair: { mode: CrosshairMode.Normal },
    rightPriceScale: { borderColor: '#2a2e39' },
    // TIME LOCALIZATION — America/New_York on the intraday panes, `Intl` doing DST (2026-08-25,
    // card t_69d27c87). SET HERE, ONCE, for the reason every other constant in this function is:
    // three panes means three copies, and three copies of a load-bearing formatter is three places
    // the next edit diverges. The decisions themselves — which zone, which spelling, and why
    // 1D/1W/1M keep the exchange's UTC date — are all in `axistime.ts`; this only installs them.
    localization: {
      // The CROSSHAIR label. Overrides `dateFormat`, deliberately: `dateFormat` is a template with
      // no zone concept at all, so it cannot express this.
      timeFormatter: (time: Time) => crosshairTimeLabel(time, tf) ?? '',
    },
    timeScale: {
      borderColor: '#2a2e39',
      // A CLOCK BELOW 1D. `timeVisible: false` on every pane is what made the 15m axis read
      // `1, 2, 1, 2` -- lightweight-charts fell back to the day number because that was the only
      // field it was allowed to print. `secondsVisible` stays false: no timeframe here is finer
      // than a minute, so seconds would be three characters of noise on every label.
      timeVisible: needsClockOnAxis(tf),
      secondsVisible: false,
      // The AXIS tick labels. The library still decides the GRANULARITY of each tick (it passes
      // `TickMarkType`); only the zone and the spelling are ours.
      tickMarkFormatter: (time: Time, tick: TickMarkType) => axisTickLabel(time, tick, tf),
      // RIGHT OFFSET — future space, load-bearing rather than cosmetic (2026-07-28). Every
      // forward-looking overlay is anchored at the bar that SET it and drawn rightwards: range
      // levels, the open leg's ray, FVG boxes, the OTE band. With `rightOffset: 0` the newest
      // bar sits flush against the price scale, so a zone anchored AT the right edge has no
      // pixels to occupy — measured on xyz:SNDK 1D, two OTE bands painted 15px and 5px wide at
      // x=881 and x=891 of an 896px pane, so a vision pass reported "no clearly rendered fill
      // zone" while both sat correctly in the draw list. Cache-wide, 81% of extended OTE bands
      // have under 10 bars of room (median 2, min 0).
      //
      // 12 is not a calibration: an OTE zone, an unfilled FVG and a dealing-range level are all
      // claims about bars that have NOT happened yet, so the space they need is future space.
      rightOffset: 12,
    },
    autoSize: true,
  };
}

/**
 * THE DOT CONVENTION — a point placed BY AN EVENT, coloured by NESTING LEVEL.
 *
 * Andrei rejected the dots twice, both times because a screenshot was matched pixel-for-pixel
 * instead of reading the rules. The rule is quoted verbatim in
 * `docs/reports/structure-points-rule-2026-08-09.md`; the two things it settles:
 *
 *   * A STRUCTURE POINT IS NOT A SWING PIVOT. Its placement is an EVENT, decided by the
 *     detector, and the server sends the result (`structure_points` on the swings payload) --
 *     so the browser draws what arrives and re-derives nothing. A second opinion here is the
 *     second-source-of-truth defect this repo keeps producing.
 *   * COLOUR ENCODES DIRECTION: rose on a high, violet on a low. See `DOT_HIGH_COLOR`.
 *
 * ONLY MAIN STRUCTURE IS DRAWN -- Andrei, 2026-08-10: "Let's focus only on the main." Nested
 * (internal) structure is not detected and not coloured; the source states when to STOP an
 * internal structure but never when to START one, so there is nothing to draw and no constant
 * reserved for it.
 *
 * HISTORY, so nobody re-litigates a settled question: this file has carried THREE colour
 * conventions. Major-vs-minor by size and hue (wrong), then direction (rejected at the time),
 * then main-vs-internal nesting. The corpus genuinely supports both of the last two -- the
 * beginners' stream separates main from internal by colour, every frame of the structure video
 * separates by side -- and Andrei chose SIDE, with "only the main" removing the other axis
 * entirely. Do not restore a nesting colour without a new instruction from him.
 */
/**
 * COLOUR ENCODES DIRECTION: a dot on a HIGH is rose, a dot on a LOW is violet.
 *
 * Andrei, 2026-08-10, with his own reference frame: "Let's focus only on the main. Red dots on
 * top, purple at the bottom." That settles a conflict the corpus really does contain -- the
 * beginners' stream distinguishes MAIN from INTERNAL structure by colour ("green and red are
 * the main ones, purple is the internal structure"), while every frame of the structure video
 * uses two colours by SIDE. We draw main structure only, so side is the only distinction left
 * to carry, and his instruction is the tie-break.
 *
 * HIS EXACT HUES ARE NOT COPIED, AND HE ASKED FOR THAT: burgundy #9E4B5C and amethyst #8A5CC0
 * are chosen for a WHITE chart. On our #131722 canvas #9E4B5C is muddy and sits close enough to
 * the down-candle #ef5350 to be read as part of the candle rather than a marker. So the hue
 * FAMILY is preserved (warm/red above, violet below) and the lightness lifted until each dot
 * separates from both the background and the candle it sits on:
 *
 *   #ff7a94  rose on a HIGH   -- clearly lighter than the #ef5350 down candle
 *   #b18cff  violet on a LOW  -- no neighbour in the candle palette at all
 *
 * The instruction that governs this file is "keep the chart structure visible for human":
 * restyling for legibility is allowed, obscuring structure is not.
 *
 * NO INTERNAL-STRUCTURE COLOUR. There was a `DOT_INTERNAL_COLOR` here, defined and unreachable,
 * waiting for a nesting trigger the source never states. "Only the main" retires it rather than
 * leaving a constant no path can produce -- one path per feature, and the detector emits main
 * structure only. If nested structure is ever specified it needs its own visual channel, not a
 * revival of this one, because colour is now spent on direction.
 */
export const DOT_HIGH_COLOR = '#ff7a94';
export const DOT_LOW_COLOR = '#b18cff';

/** ONE size for every dot. Uniform is the point -- importance is carried by the level text. */
export const DOT_SIZE = 1.0;

/**
 * One structure point as `chartapp/api/overlays.py::swings` serves it.
 *
 * Declared here rather than beside `SwingsPayload` because the payload type lives in
 * `overlays.ts`, which another change owns. `structurePointDots` takes the field structurally,
 * so a whole `SwingsPayload` satisfies it.
 */
export type StructurePoint = {
  idx: number;
  time: Time;
  price: number;
  /** true == an upper point (a high), false == a lower one. Points alternate. */
  is_upper: boolean;
  /** Nesting: 0 == main structure. Always 0 today. */
  level: number;
};

/**
 * The dots, straight from the payload. NO DERIVATION AND NO FILTERING.
 *
 * A pivot with no structure point gets NOTHING -- that absence is the rule, and it is the
 * single property that separates this convention from both previous ones.
 *
 * A DOT SITS ON THE WICK EXTREME, not offset with a gap, and `is_upper` picks the side.
 * In the reference the wick tip disappears INSIDE the circle. lightweight-charts v4 gives three
 * positions and only one of them can do that: `aboveBar`/`belowBar` compute
 * `priceToCoordinate(high) - halfSize - offsets.aboveBar` (library source, `fillSizeAndY`), so the
 * dot is ALWAYS at least a half-shape plus `shapeMargin` (min 3px) clear of the extreme, and the
 * gap GROWS with each coincident marker. `inBar` anchors on `close`, which is mid-body and worse.
 * So there is no library position that centres on the extreme, and the honest thing is to pick
 * the one that stays ATTACHED to the correct side and say so.
 *
 * That is also why `price` is carried on the payload but not on the marker: markers anchor to a
 * BAR and a side, never to a price. The price reaches the screen as the side, and the pane's
 * own candles put it at the extreme it names.
 *
 * ponytail: the residual few-pixel gap is lightweight-charts' marker geometry, not ours. Closing
 * it needs a custom ISeriesPrimitive drawing circles at `priceToCoordinate(price)` -- a second
 * coordinate implementation, which is the code path that shipped broken three times on the break
 * labels. Upgrade to a primitive only if he says the gap still reads wrong.
 */
export function structurePointDots(p: { structure_points?: StructurePoint[] }): SeriesMarker<Time>[] {
  const dots: SeriesMarker<Time>[] = (p.structure_points ?? []).map((sp) => ({
    time: sp.time,
    position: sp.is_upper ? ('aboveBar' as const) : ('belowBar' as const),
    shape: 'circle' as const,
    color: sp.is_upper ? DOT_HIGH_COLOR : DOT_LOW_COLOR,
    size: DOT_SIZE,
    // NO CAPTION. In the reference the dots carry no text at all; the IDM label is a separate
    // mark, and a second word in the same slot is the "BOSSH" collision.
    text: '',
  }));
  // lightweight-charts silently DROPS out-of-order markers, so an unsorted list is a
  // disappearing dot rather than an error.
  dots.sort((a, b) => (a.time as number) - (b.time as number));
  return dots;
}

/**
 * The INDUCEMENT dot's label: the TIMEFRAME.
 *
 * IDENTIFIED BY ITS TEXT, which needs saying plainly because it looks fragile. The merged list
 * holds two kinds of dot and `SeriesMarker` has no field for "which builder made me": structure
 * points carry `text: ''` by contract (a second word beside the SH/SL arrow is the "BOSSH"
 * collision that made those labels conditional), and the inducement dot is the only dot with any
 * text at all. So non-empty text IS the inducement, and the textless side is asserted in the
 * tests so the branch cannot rot unnoticed.
 *
 * IT ONLY TOUCHES THE TEXT NOW. It used to recolour every dot by DIRECTION, which was the wrong
 * convention (see `DOT_MAIN_COLOR`) and also the wrong place: a structure point's colour comes
 * from its nesting level, which is a payload field, so the builder can set it and nothing has to
 * be repainted on the way to the series. What CANNOT be built in `overlays.ts` is `IDM 15m` --
 * that names a TIMEFRAME and those builders are handed a payload carrying none. Only the pane
 * knows its own, which is the entire reason this function is here.
 */
export function dotStyle(d: SeriesMarker<Time>, tf: string): SeriesMarker<Time> {
  if ((d.text ?? '') === '') return d;
  // THE TIMEFRAME, NOT THE PRICE. We printed `idm 64865`; his reads `IDM 15m`. The price is
  // already on the axis and in the zone label, and repeating it eight times down a pane is how
  // the label mat happened. The timeframe is the one thing the dot's position does NOT say --
  // an inducement found on 15m looks identical to one found on 1D once it is a dot on a bar.
  return { ...d, text: `IDM ${tf}` };
}

/**
 * Is this marker on a bar this pane actually HOLDS?
 *
 * *** THE LEFT-EDGE `idm <price>` COLUMN. *** Andrei, 2026-08-09: *"review dots on 15m (no idea
 * where this docs column came from)."* It is a neat evenly-spaced vertical stack of `idm <price>`
 * labels pinned to ONE x-coordinate down the left edge of the 15m pane, over empty background
 * with no candles beneath, reading like a dumped legend.
 *
 * THE CAUSE IS NOT A PRICE PROBLEM, and the lead I was given (`main.ts:1465-1490`, the "zones off
 * screen" feature, whose comment notes `priceToCoordinate` extrapolates) is the wrong suspect:
 * that code widens a PRICE SCALE and touches no marker. Markers do not use `priceToCoordinate`
 * for their x at all. Read the library instead --
 * `node_modules/lightweight-charts/dist/lightweight-charts.development.mjs`:
 *
 *   * `_private__recalculateMarkers` (:5033) calls `timeScale.timeToIndex(marker.time, TRUE)`.
 *     That second argument is `findNearest`, and `timeToIndex` (:6742) with it set returns the
 *     NEAREST edge index for a time outside the series instead of `null`: a time before the
 *     first bar returns index 0, a time after the last returns the last index.
 *   * so EVERY marker whose bar is not in this pane's window is silently relocated onto the
 *     FIRST bar (or the last). It is not clipped and it is not dropped.
 *   * `_internal_update` (:3728) then stacks markers that share a bar: `offsets.aboveBar` and
 *     `offsets.belowBar` are reset only `if (marker.time !== prevTimeIndex)` and otherwise grow
 *     by `shapeSize + shapeMargin` plus a text line per marker. N markers forced onto bar 0
 *     therefore paint as an evenly-spaced vertical column at bar 0's x. THAT IS THE COLUMN,
 *     mechanism and all -- and it explains every detail of it: one x-coordinate (bar 0), even
 *     spacing (a constant per-marker offset), left edge (index 0 is the nearest edge for
 *     anything older), no candles beneath (the column climbs off the price scale entirely
 *     because the offsets are pixels, not prices), and prices that INCREASE downward while the
 *     axis decreases (the stack order is marker order, so it has no relation to price at all --
 *     which is exactly why several values sit outside the pane's range).
 *
 * WHY OFF-WINDOW MARKERS EXIST AT ALL: the setup route is fetched with `&first_time=` only when
 * `pane.windowFirstTime` is set (`main.ts:2132`), so a pane whose window is unset -- or whose
 * detector returns rows older than the window it asked for -- receives inducements for bars this
 * pane never loaded. `main.ts:2252` appends them all.
 *
 * FIXED AT THE MERGE, not per builder: every marker source lands on the same clamp, and a guard
 * in `idmDots` alone would leave swing, break and liquidity markers still able to column up.
 * An honest absence -- a level we cannot place on a real bar is not drawn -- rather than a
 * confident wrong pixel.
 *
 * Empty `bars` means the pane has no window yet, so nothing is claimed and nothing is dropped:
 * markers set before the first `setBars` are the normal order during a load.
 */
export function insideWindow(m: SeriesMarker<Time>, bars: readonly Bar[]): boolean {
  if (bars.length === 0) return true;
  const t = m.time as number;
  return t >= bars[0].time && t <= bars[bars.length - 1].time;
}

export class ChartPane {
  readonly id: string;
  readonly container: HTMLElement;
  readonly chart: IChartApi;
  /** The writable series. PRIVATE so `setBars` / `updateBar` are the only candle-write path.
   *
   * Consumers get `candleSeries` below, which is the same object with `setData`/`update` typed away. */
  private readonly writableSeries: ISeriesApi<'Candlestick'>;

  /** The series, minus the two candle-write methods. See `PaneSeriesReadonly`. */
  readonly candleSeries: PaneSeriesReadonly;

  /** Per-pane primitives. See the module docstring for why these cannot be shared. */
  readonly range = new RangePrimitive();
  readonly breaks = new BreakPrimitive();
  readonly levels = new LevelPrimitive();
  readonly drawings = new DrawingPrimitive();

  /** This pane's timeframe. The asset and source are global. */
  tf: string;

  /**
   * Where this pane writes its time-zone mark, or null when it has none.
   *
   * OPTIONAL BY DESIGN: `chartpane.test.ts` runs in plain node with no DOM (no jsdom in this repo's
   * vitest setup), and `zoneTag`/`zoneTagTitle` are pure functions tested directly. A pane with no
   * slot formats its labels exactly the same way — the mark is a disclosure, not a control.
   */
  zoneSlot: HTMLElement | null = null;

  /** This pane's overlay toggles — ICT analysis is per timeframe. */
  overlays: PaneOverlays;

  /** The bars currently rendered, for the magnet snap and the OHLC header.
   *
   * INCLUDES THE FORMING BAR, and the docstring used to deny it. `/api/bars` serves the live
   * candle deliberately (`chartapp/api/main.py:405-412` -- "the chart must draw the live candle")
   * and `updateBar` appends or replaces it on every poll, so `bars[bars.length - 1]` is the
   * unclosed bar whenever the source is live. `lastBarForming` below is how a consumer tells
   * which. Corrected 2026-08-17 (t_b68b2caa): a reader trusting "closed" here would have
   * published a settled change figure over an interval that had not finished.
   */
  bars: Bar[] = [];

  /** Does `bars[bars.length - 1]` belong to a bar that has NOT closed?
   *
   * Set from `meta.has_forming_bar` on every load and from the live poll -- NEVER derived by
   * comparing wall-clock to `tf`. The boundary is the server's (`split_forming`), and a second
   * implementation of "is this bar closed" is the second-source-of-truth this app forbids.
   *
   * PER PANE, unlike `closedBarCount` in `main.ts`, which describes the execution pane only.
   */
  lastBarForming = false;

  /**
   * The first timestamp of the window this pane is showing, or null for "everything".
   *
   * Sent to the overlay routes so they return rows for the SAME window `/api/bars` served. Pivots
   * carry `idx`/`confirm_idx` — offsets into the bar array — so a pane holding 600 bars must not be
   * handed pivots computed against 5,000, or every index points at the wrong candle.
   */
  windowFirstTime: number | null = null;
  /** The last timestamp of the window this pane is showing, or null. Sent as `last_time` on the
   *  research source, whose overlays are bounded on both sides (card t_42cdbde6). */
  windowLastTime: number | null = null;
  /** The navigator's anchor: bars load `around` this epoch second instead of at the live edge,
   *  or null for the live edge. A manual asset or source change clears it (card t_42cdbde6). */
  around: number | null = null;

  /**
   * Decimal places the price axis, crosshair label and last-price tag print for THIS pane's asset,
   * or null before the first load. Read back by tests and by `main.ts`'s zone hook; SET only through
   * `setPriceDecimals`, which is what applies it to the series.
   */
  priceDecimals: number | null = null;

  /**
   * Apply the asset's price precision to the candle series (card `t_ccfe3378`).
   *
   * THE SERIES IS REUSED ACROSS ASSETS -- `setBars` replaces the data, never the series -- so a
   * format set once at construction would be BTC's for the rest of the session and a BTC -> PUMP
   * switch would keep an axis of `0.00`. Every bars load routes through here with the server's
   * `meta.px_decimals` (see `pricescale.precisionFor`), the same way every timeframe change routes
   * through `setTimeframe`: one method, or the next asset switch repeats the defect.
   *
   * `null` is "no answer" and leaves the series as it is. It must NOT be turned into 2 here: 2 is
   * the library default that produced the defect, and a caller with no number is a caller whose
   * fallback (`decimalsFromValues` over the closes) already found nothing readable.
   *
   * Returns whether the series' options were touched, so a test can tell "re-applied on switch"
   * from "the value happened to be the same".
   */
  setPriceDecimals(decimals: number | null): boolean {
    if (decimals === null) return false;
    const fmt = priceFormatFor(decimals);
    if (this.priceDecimals === fmt.precision) return false;
    this.priceDecimals = fmt.precision;
    this.writableSeries.applyOptions({ priceFormat: fmt });
    return true;
  }

  /**
   * Markers from every marker-producing overlay on THIS pane, merged and time-sorted.
   *
   * PER PANE, and that is a bug fix rather than a design flourish. `markerState` was a MODULE
   * global while `redrawMarkers()` called `candleSeries.setMarkers(...)` on the single global
   * series, so with three panes every pane's overlay run wrote one shared list onto one chart.
   * Andrei saw the result: the 15m pane's ~1,300 swing pivots painted onto the 1D chart as a
   * dense field of markers that "disappeared when I switch to another timeframe" — because the
   * next pane's refresh overwrote the same list. It also explains why the swings checkbox looked
   * dead on 1W and 1D: those panes DID compute their markers, then a later pane's run clobbered
   * them before anything could be seen.
   *
   * lightweight-charts keeps ONE marker list per series, so the three sources still have to be
   * merged — but merged PER SERIES, which is what this makes possible.
   */
  markers: {
    swings: SeriesMarker<Time>[];
    liquidity: SeriesMarker<Time>[];
    breaks: SeriesMarker<Time>[];
    /** Structure points and the inducement dot. A FOURTH source in the SAME
     *  per-pane record, not a field of its own: the clobber bug above was a shared
     *  list, so a dot list that lived anywhere but here would reproduce it exactly. */
    dots: SeriesMarker<Time>[];
    /** LTF evidence events (card `t_bf04f7ec`). A FIFTH source, HERE for the same reason the
     *  fourth is: a `ltfMarkers` list kept anywhere else would be the shared-global clobber
     *  again, and this layer's markers are per (asset, timeframe) exactly like the rest. */
    ltf: SeriesMarker<Time>[];
    /** Session model `M` / `D` (card `t_5b2017b5`). A SIXTH source, here for the same reason. */
    sessions: SeriesMarker<Time>[];
    /** Static structure points / breaks / fractals (card `t_fb5e844d`). A SEVENTH source. */
    structure: SeriesMarker<Time>[];
    /** BP sweep / 50% / outcome markers (card `t_5ee634f0`). An EIGHTH source. */
    bp: SeriesMarker<Time>[];
    /** BP imbalance merged-run markers (card `t_28a039b9`). A NINTH source. */
    bpImb: SeriesMarker<Time>[];
    /** BP 021 IDM formed / broken markers (card `t_6f7db3f5`). A TENTH source. */
    bpIdm: SeriesMarker<Time>[];
    /** Experiment event markers (card t_42cdbde6). An ELEVENTH source. */
    experiment: SeriesMarker<Time>[];
  } = {
    swings: [],
    liquidity: [],
    breaks: [],
    dots: [],
    ltf: [],
    sessions: [],
    structure: [],
    bp: [],
    bpImb: [],
    bpIdm: [],
    experiment: [],
  };

  /** FVG / liquidity price lines on THIS pane, tracked so a refresh can remove them.
   *  Same shared-global bug as `markers`: one pane's refresh was removing another's lines. */
  priceLines: IPriceLine[] = [];

  /**
   * The hand-drawn set the SERVER holds for (asset, THIS pane's tf), as last rendered.
   *
   * PER PANE, and like `markers` above that is a bug fix rather than symmetry. `drawState` and a
   * `drawPrimitive` singleton lived in `main.ts`, and the singleton was attached to the EXECUTION
   * pane's series only -- so `chart.subscribeClick` existed on one chart and the 1W and 1D panes
   * had no drawing layer at all. Andrei: "our drawing tools like lines, rectangle, Fib levels,
   * etc are not working on our panes." Measured before the fix: two clicks with the trendline
   * armed advanced the hint on 15m and did literally nothing on 1W or 1D.
   *
   * It cannot be shared even in principle: `/api/drawings/{asset}/{tf}` is keyed on the TIMEFRAME,
   * so three panes showing 1W/1D/15m are three different stored sets, and `DrawingPrimitive` holds
   * `_chart`/`_series`/`_selected` per instance, so one object cannot serve three series.
   */
  drawState: DrawingsPayload | null = null;

  /**
   * Anchors collected so far for an in-progress drawing ON THIS PANE.
   *
   * The ARMED TOOL stays global in `main.ts` (one tool at a time across the app), but a
   * half-finished drawing is a position on one price/time grid, so it belongs to the pane whose
   * grid it was clicked on. `main.ts` keeps at most one pane non-empty here -- clicking in another
   * pane abandons the partial drawing rather than mixing anchors from two timeframes into one
   * shape, which would place a trend line through coordinates that never appeared together.
   */
  drawPending: Anchor[] = [];

  /** Switch this pane's timeframe, and re-derive everything that depends on it.
   *
   * A SETTER, NOT A BARE FIELD ASSIGNMENT. `main.ts` did `pane.tf = sel.value`, which updated the
   * field and left the CHART configured for the old timeframe -- so switching 1D -> 15m kept a
   * date-only axis, and the `1, 2, 1, 2` labels came back on a pane that had been correct at
   * construction. Anything derived from `tf` has to be re-applied in one place that every caller
   * routes through, or the next derived option repeats the bug.
   */
  setTimeframe(tf: string): void {
    this.tf = tf;
    // BOTH FORMATTERS ARE RE-APPLIED, and that is the same bug one level up. `chartOptions`
    // CLOSES OVER `tf`, so the callbacks installed at construction keep formatting for the OLD
    // timeframe forever: a pane built at 1D and switched to 15m would print its clock ticks in
    // the exchange's UTC (the 1D rule) while its axis showed intraday bars — a silent 4-5 hour
    // error, i.e. exactly the defect this card exists to remove, reintroduced by a stale closure.
    this.chart.applyOptions({
      timeScale: {
        timeVisible: needsClockOnAxis(tf),
        tickMarkFormatter: (time: Time, tick: TickMarkType) => axisTickLabel(time, tick, tf),
      },
      localization: {
        timeFormatter: (time: Time) => crosshairTimeLabel(time, tf) ?? '',
      },
    });
    this.renderZoneTag();
  }

  /**
   * Write the zone mark into this pane's own slot, if it has one.
   *
   * THE AXIS MUST SAY WHICH ZONE IT IS IN. An unmarked axis is the same silent divergence the UTC
   * labels were, pointing the other way: a reader who knows the app was fixed still cannot tell,
   * from the pane, whether THIS timeframe is NY or UTC — and the answer differs per pane by design.
   *
   * ONE MARK PER PANE, not a suffix on every tick: `.pane-bar` is already the busiest row in the UI
   * (see `index.html`'s measured wrap edges) and three characters on every axis label is the label
   * mat this app has paid for before.
   *
   * IT LIVES ON THE PANE, next to `setTimeframe`, because the tag must change WITH the timeframe.
   * A `renderZoneTag(pane)` in `main.ts` would be a second thing every tf-change path has to
   * remember to call, and `setTimeframe` exists precisely because `pane.tf = x` forgot one.
   */
  renderZoneTag(): void {
    const slot = this.zoneSlot;
    if (!slot) return;
    slot.textContent = zoneTag(this.tf);
    slot.title = zoneTagTitle(this.tf);
  }

  constructor(opts: { id: string; container: HTMLElement; tf: string; overlays?: PaneOverlays }) {
    this.id = opts.id;
    this.container = opts.container;
    this.tf = opts.tf;
    this.overlays = { ...(opts.overlays ?? DEFAULT_OVERLAYS) };

    this.chart = createChart(this.container, chartOptions(opts.tf));

    this.writableSeries = this.chart.addCandlestickSeries({
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderUpColor: '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });

    // The SAME object, exposed without the two candle-write methods. No wrapper and no proxy: the
    // narrowing is purely a compile-time view, so there is nothing to keep in sync at runtime.
    this.candleSeries = this.writableSeries;

    // Attached ONCE per pane, never per refresh. A primitive attached inside the refresh path
    // would need a matching detach, and a missed one leaves a second copy of the geometry drawing
    // over the first. `Drawing` additionally installs pointer listeners in `attached()`, so a
    // re-attach would stack duplicate handlers and one drag would move an anchor twice.
    this.candleSeries.attachPrimitive(this.range);
    this.candleSeries.attachPrimitive(this.breaks);
    this.candleSeries.attachPrimitive(this.levels);
    this.candleSeries.attachPrimitive(this.drawings);
  }

  /** Replace the rendered series. Volume is optional; absent stays absent. */
  setBars(bars: Bar[]): void {
    this.bars = bars;
    // Flags computed over the WHOLE series, because the anchor persists across bars — see
    // `insideFlags`. A per-bar predicate cannot express "until something takes out the anchor".
    const inside = insideFlags(bars);
    this.writableSeries.setData(bars.map((b, i) => tintInside(b, inside[i])));
  }

  /** Merge one live bar into this pane's series, in place.
   *
   * SEPARATE FROM `setBars` because `setData()` on every poll would reset the viewport and discard
   * the user's zoom — the whole reason the main chart uses `update()` for the forming bar.
   *
   * REFUSES AN OLDER BAR, which is not a style choice: lightweight-charts throws
   * "Cannot update oldest data" if `update()` is handed a time before the series' last, so a stale
   * response arriving out of order would take the pane down. The main chart learned this by driving
   * the real UI (see `pollLive`); this is the same rule, not a second implementation of it — the
   * ordering decision lives here, once, where the series is.
   *
   * Returns whether the bar was applied, so a caller can tell "ignored, too old" from "drawn".
   */
  updateBar(b: Bar): boolean {
    const last = this.bars.length ? this.bars[this.bars.length - 1] : undefined;
    if (last !== undefined && b.time < last.time) return false;

    // Update `bars` FIRST, then recompute the flag from the same list `setBars` would see. The anchor
    // persists across bars (`insideFlags`), so the live bar's status depends on the last COLOURED
    // candle, which may be many bars back — not on `bars[-2]`, which is all a pairwise rule needed.
    // Deriving both paths from one function is what keeps the right edge consistent with the series.
    const replacing = last !== undefined && b.time === last.time;
    if (replacing) this.bars[this.bars.length - 1] = b;
    else this.bars = [...this.bars, b];

    const flags = insideFlags(this.bars);
    this.writableSeries.update(tintInside(b, flags[flags.length - 1]));
    return true;
  }

  /** Merge this pane's marker sources and hand them to ITS series.
   *
   * TWO THINGS HAPPEN HERE THAT CANNOT HAPPEN ANYWHERE ELSE, and both are bug fixes:
   *   1. `dotStyle` puts the pane's own timeframe on the inducement label. The dot builders
   *      have no idea which timeframe they are being drawn on, so `IDM 15m` is not
   *      expressible there. The pane is the only object that holds `tf`.
   *   2. `insideWindow` drops markers that fall outside the bars this pane is actually
   *      showing. See that function for the measured cause of the left-edge column.
   * Both run at the ONE choke point every marker source routes through, so a fifth source
   * gets them for free rather than needing its own copy.
   *
   * NO DOT CAP. There was a flat ceiling of 14 here, standing in for the structural-validity
   * rule the detector did not have. It has one now (structure points are placed by an event),
   * and the count falls out of it: MEASURED on real BTC 15m bars, 49 points over 5,003 bars --
   * 0 to 14 per 600-bar window, median 8. A cap of 14 would clip a real window, so it went.
   *
   * IT RETURNS WHAT THE SERIES WAS GIVEN, so a disclosure row can be counted from the canvas
   * rather than from a builder's output (R4-6, high). The liquidity sidebar said
   * `sweeps 3 of 3 marked` while one marker reached the series, because the row was computed
   * before this filter ran and nothing downstream could see the difference. The filtered list
   * is now the return value: whoever states a count reads THIS, and a second `insideWindow`
   * call in a summary function would be the two-sources-of-truth defect again.
   */
  redrawMarkers(): SeriesMarker<Time>[] {
    const merged = [
      ...this.markers.swings,
      ...this.markers.liquidity,
      ...this.markers.breaks,
      ...this.markers.dots.map((d) => dotStyle(d, this.tf)),
      ...this.markers.ltf,
      ...this.markers.sessions,
      ...this.markers.structure,
      ...this.markers.bp,
      ...this.markers.bpImb,
      ...this.markers.bpIdm,
      ...this.markers.experiment,
    ].sort((a, b) => (a.time as number) - (b.time as number));
    const drawn = merged.filter((m) => insideWindow(m, this.bars));
    this.candleSeries.setMarkers(drawn);
    return drawn;
  }

  /** Replace this pane's price lines, removing the previous set first. */
  setPriceLines(specs: Partial<PriceLineOptions>[]): IPriceLine[] {
    for (const l of this.priceLines) this.candleSeries.removePriceLine(l);
    this.priceLines = specs.map((sp) => this.candleSeries.createPriceLine(sp as PriceLineOptions));
    return this.priceLines;
  }

  /**
   * Clear every overlay this pane draws.
   *
   * Called before a timeframe or asset change so stale geometry cannot survive onto a new series.
   * That is not hypothetical tidiness: a dealing-range level from 1W painted over a 15m series
   * would be a confidently wrong picture — a price level that looks computed and belongs to a
   * different chart. Each primitive already defaults to empty for the same reason.
   */
  clearOverlays(): void {
    this.range.setData({ rays: [], bands: [] });
    this.breaks.setData([]);
    this.breaks.setZigzag(null);
    this.levels.setData([]);
    this.markers = { swings: [], liquidity: [], breaks: [], dots: [], ltf: [], sessions: [], structure: [], bp: [], bpImb: [], bpIdm: [], experiment: [] };
    this.redrawMarkers();
    this.setPriceLines([]);
    // THE HAND-DRAWN LAYER TOO, because it is keyed by (asset, tf) exactly like the computed
    // geometry above: a trend line anchored at weekly bars is not a claim about the 15m series this
    // pane is about to show. Every series change funnels through here (`loadPaneBars`, `loadBars`,
    // the timeframe select), so clearing here is what stops the old timeframe's drawings from
    // surviving onto the new one -- `main.ts` refetches for the new (asset, tf) afterwards.
    this.drawings.setData([]);
    this.drawings.setSelected(null);
    this.drawState = null;
    this.drawPending = [];
  }

  /** Release the chart. lightweight-charts leaks its canvas and observers without this. */
  destroy(): void {
    this.chart.remove();
  }
}

/**
 * Put the order ticket's levels, READ-ONLY, on every pane EXCEPT the execution one.
 *
 * Andrei: *"When trade setup is happening, I should see trade parameter levels on all 3
 * timeframes."* A stop chosen off a 15m swing is only trustworthy if you can see where it sits
 * against the weekly structure.
 *
 * `TicketPrimitive` IS DELIBERATELY NOT REUSED FOR THIS, and that is the whole reason this
 * function exists rather than a second `attachPrimitive` call. The ticket primitive installs its
 * pointer listeners in `attached()`, so attaching it to three panes stacks duplicate handlers and
 * one drag moves the level two or three times. `LevelPrimitive` is its sibling -- same plates,
 * drawn by the shared `levelplate` -- and every pane already owns one, attached ONCE in the
 * constructor, so nothing is constructed here either.
 *
 * IT IS NO LONGER READ-ONLY (2026-08-18, card t_35eac373). A level carrying an `id` can be
 * dragged, and `livelevels.withDragHandles` puts one on exactly two levels: the LIVE position's
 * SL and TP. Andrei asked for the stop to be movable "on all panes", so the context panes get the
 * handles too -- with the gesture shared through `legdrag.ts` rather than reimplemented, and
 * pixel->price done by each pane's OWN series. Everything else drawn here is still ungrabbable.
 *
 * THE EXECUTION PANE IS SKIPPED, not written with an empty list: its `levels` primitive belongs to
 * the account panel (`tpDrawLevels` draws the LIVE position's entry/SL/TP there), and the ticket's
 * draggable legs already cover the same prices on that pane. Writing the ticket's levels there
 * would both overprint those legs -- a bug already fixed once, measured on the live xyz:AMZN short
 * -- and silently wipe the live position's levels.
 *
 * PASSING AN EMPTY LIST CLEARS. A stale entry line on the 1W chart after the ticket is emptied is
 * worse than no line: it is a price he might act on.
 *
 * Takes the registry as an argument rather than reading a module global, for the reason the
 * deleted `activePane()` aliases record: anything that must act on every pane should not be
 * writable by accident as something that acts on one.
 */
export function drawContextLevels(
  registry: Iterable<ChartPane>,
  executionPaneId: string,
  levels: AccountLevel[],
): void {
  for (const pane of registry) {
    if (pane.id === executionPaneId) continue;
    pane.levels.setData(levels);
  }
}
